<template>
  <div id="app">
    <Header />
    <!-- ScrollSmoother 结构；position: fixed 元素须留在 wrapper 外 -->
    <div id="smooth-wrapper">
      <div id="smooth-content">
        <router-view />
      </div>
    </div>
  </div>
</template>

<style>
#app {
  min-height: 100vh;
  /* 手机端实时可见高度（JS 写入） */
  min-height: var(--app-height, 100vh);
  display: flex;
  flex-direction: column;
  /* iPhone 四边安全区补偿 */
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
}
</style>

<script>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollSmoother } from 'gsap/ScrollSmoother'
import { Observer } from 'gsap/Observer'
import Header from './components/Header.vue'

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, Observer)

export default {
  name: 'App',
  components: { Header },
  // 滚动吸附与高度同步的运行时状态
  data() {
    return {
      isScrolling: false,   // 滚动/惯性进行中
      isTouching: false,    // 手指按住屏幕
      heightPending: false, // 待同步的可见高度
      scrollEndTimer: null, // 滚动停止判定定时器
      lastAppHeight: 0,     // 上次写入的 --app-height
      smoother: null,       // ScrollSmoother 实例
      snapTrigger: null,    // ScrollTrigger 吸附实例
      pageObserver: null,   // 滚轮翻页 Observer
      pageTween: null,      // 翻页/回弹动画
      // 触摸翻页手势状态
      turnTouchActive: false,  // 手势已接管翻页
      turnTouchSkip: false,    // 本次触摸不接管
      turnTouchStartY: 0,      // 起点 clientY
      turnTouchLastY: 0,       // 上一采样点 clientY
      turnTouchLastTime: 0,    // 上一采样时间
      turnTouchVelocity: 0,    // 滑动速度（px/ms）
      turnTouchPeakVelocity: 0,// 速度峰值
      turnTouchStartScroll: 0, // 起点滚动位置
      turnTouchInner: null     // 起点所在内层滚动容器
    }
  },
  created() {
    // 同步 <html lang> 与页面元信息
    this.syncLang()
    this.$watch(() => this.$i18n.locale, this.onLocaleChange)
  },
  mounted() {
    // ignoreMobileResize：地址栏收展不刷新滚动位置，避免吸附点位移
    this.smoother = ScrollSmoother.create({
      smooth: 1,
      ignoreMobileResize: true
    })
    // 整屏吸附统一管理
    this.updateSnap()
    this.unwatchRoute = this.$watch(() => this.$route.path, this.updateSnap)
    // 用可见高度写入 --app-height，代替 100vh
    this.updateAppHeight()
    // 滚动/触摸期间挂起高度同步，滚停后统一处理
    window.addEventListener('resize', this.onViewportChange)
    window.addEventListener('orientationchange', this.onOrientationChange)
    window.addEventListener('scroll', this.onPageScroll, { passive: true })
    window.addEventListener('touchstart', this.onTouchStart, { passive: true })
    window.addEventListener('touchend', this.onTouchEnd, { passive: true })
    window.addEventListener('touchcancel', this.onTouchEnd, { passive: true })
    // touchmove 需非 passive 才能 preventDefault
    window.addEventListener('touchstart', this.onTurnTouchStart, { passive: true })
    window.addEventListener('touchmove', this.onTurnTouchMove, { passive: false })
    window.addEventListener('touchend', this.onTurnTouchEnd, { passive: true })
    window.addEventListener('touchcancel', this.onTurnTouchEnd, { passive: true })
  },
  beforeDestroy() {
    // 依次销毁动画与实例
    if (this.pageTween) {
      this.pageTween.kill()
      this.pageTween = null
    }
    if (this.pageObserver) {
      this.pageObserver.kill()
      this.pageObserver = null
    }
    if (this.snapTrigger) {
      this.snapTrigger.kill()
      this.snapTrigger = null
    }
    if (this.smoother) {
      this.smoother.kill()
      this.smoother = null
    }
    window.removeEventListener('resize', this.onViewportChange)
    window.removeEventListener('orientationchange', this.onOrientationChange)
    window.removeEventListener('scroll', this.onPageScroll)
    window.removeEventListener('touchstart', this.onTouchStart)
    window.removeEventListener('touchend', this.onTouchEnd)
    window.removeEventListener('touchcancel', this.onTouchEnd)
    window.removeEventListener('touchstart', this.onTurnTouchStart)
    window.removeEventListener('touchmove', this.onTurnTouchMove)
    window.removeEventListener('touchend', this.onTurnTouchEnd)
    window.removeEventListener('touchcancel', this.onTurnTouchEnd)
    clearTimeout(this.scrollEndTimer)
    if (this.unwatchRoute) {
      this.unwatchRoute()
    }
    document.documentElement.style.scrollSnapType = ''
    document.body.style.scrollSnapType = ''
  },
  methods: {
    syncLang() {
      // <html lang> 供 :lang() 选择器使用
      document.documentElement.lang = this.$i18n.locale === 'zh' ? 'zh-CN' : 'en'
      // 运行时同步标题与描述；静态值由构建模式输出（见 vue.config.js）
      const title = this.$t('pageTitle')
      const shareTitle = this.$t('pageShareTitle')
      const desc = this.$t('pageDescription')
      document.title = title
      this.updateMeta('name', 'description', desc)
      this.updateMeta('property', 'og:title', shareTitle)
      this.updateMeta('property', 'og:description', desc)
    },
    // 语言切换会改变页面高度（如英文站隐藏模块八），需重算滚动终点
    onLocaleChange() {
      this.syncLang()
      this.$nextTick(() => {
        // 进行中的翻页以旧终点为目标，先终止
        if (this.pageTween) {
          this.pageTween.kill()
          this.pageTween = null
        }
        ScrollTrigger.refresh()
        // 原点超出新的滚动上限时夹回，避免停在不可达位置
        const maxScroll = this.getMaxScroll()
        const current = this.smoother ? this.smoother.scrollTop() : window.scrollY
        if (current > maxScroll) {
          if (this.smoother) {
            this.smoother.scrollTop(maxScroll)
          } else {
            window.scrollTo(0, maxScroll)
          }
        }
        this.fixSnapAlignment()
      })
    },
    // 写入 meta，不存在则创建
    updateMeta(attr, key, content) {
      let el = document.head.querySelector('meta[' + attr + '="' + key + '"]')
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    },
    // 写入可见高度；有变化时返回 true
    updateAppHeight() {
      const style = getComputedStyle(document.documentElement)
      const topInset = parseFloat(style.getPropertyValue('--safe-top')) || 0
      const bottomInset = parseFloat(style.getPropertyValue('--safe-bottom')) || 0
      const effectiveHeight = window.innerHeight - topInset - bottomInset
      if (effectiveHeight === this.lastAppHeight) {
        return false
      }
      this.lastAppHeight = effectiveHeight
      document.documentElement.style.setProperty('--app-height', effectiveHeight + 'px')
      return true
    },
    // 同步挂起的高度，并按锚点纠正当前屏位置
    syncAppHeight() {
      this.heightPending = false
      // 动画中改高度会错位，结束后再同步
      if (this.pageTween) {
        this.heightPending = true
        return
      }
      // 记录锚点，改高度后平移滚动位置保持画面不动
      const anchor = this.getViewportAnchor()
      if (!this.updateAppHeight()) {
        return
      }
      const before = this.smoother ? this.smoother.scrollTop() : window.scrollY
      this.restoreViewportAnchor(anchor)
      const after = this.smoother ? this.smoother.scrollTop() : window.scrollY
      // 同步手势基准，避免继续拖动时跳位
      if (this.turnTouchActive) {
        this.turnTouchStartScroll += after - before
      }
      // 手势/滚动中不纠偏，滚停后兜底
      if (!this.turnTouchActive && !this.isScrolling) {
        this.fixSnapAlignment()
      }
    },
    // 可滚动总距离；触摸端以 --app-height 为准，避免上限偏小
    getMaxScroll() {
      const viewport = Observer.isTouch > 0 && this.lastAppHeight > 0 ? this.lastAppHeight : window.innerHeight
      return Math.max(0, document.documentElement.scrollHeight - viewport)
    },
    // 记录当前屏及其在视口上的偏移
    getViewportAnchor() {
      let anchor = null
      document.querySelectorAll('.fullscreen-section').forEach((el) => {
        // 跳过未渲染（display:none，如英文站隐藏的模块八），避免产生非法锚点
        if (!el.getClientRects().length) {
          return
        }
        const rectTop = el.getBoundingClientRect().top
        if (rectTop <= 1) {
          anchor = { el, offset: -rectTop }
        }
      })
      return anchor
    },
    // 按锚点平移滚动位置，保持当前画面不动
    restoreViewportAnchor(anchor) {
      // 元素已被路由移除时不处理
      if (!anchor || !anchor.el.isConnected) {
        return
      }
      const base = this.smoother ? this.smoother.scrollTop() : window.scrollY
      const top = base + anchor.el.getBoundingClientRect().top
      const target = Math.min(this.getMaxScroll(), Math.max(0, top + anchor.offset))
      if (Math.abs(target - base) < 1) {
        return
      }
      if (this.smoother) {
        this.smoother.scrollTop(target)
      } else {
        window.scrollTo(0, target)
      }
    },
    // 视口尺寸变化：滚动/触摸中挂起，静止时防抖同步
    onViewportChange() {
      this.heightPending = true
      // 触摸端立即同步，避免翻页目标被截断
      if (Observer.isTouch > 0) {
        this.syncAppHeight()
      }
      if (this.isScrolling || this.isTouching) {
        return
      }
      this.scheduleScrollEnd()
    },
    // 横竖屏切换：重置滚动状态后走滚停流程
    onOrientationChange() {
      this.heightPending = true
      this.isScrolling = false
      this.isTouching = false
      this.scheduleScrollEnd()
    },
    onPageScroll() {
      this.isScrolling = true
      this.scheduleScrollEnd()
    },
    // 按住期间不做吸附判定
    onTouchStart() {
      this.isTouching = true
    },
    onTouchEnd() {
      this.isTouching = false
      this.scheduleScrollEnd()
    },
    // 滚动静默 140ms 后：同步高度并纠正吸附位置
    scheduleScrollEnd() {
      clearTimeout(this.scrollEndTimer)
      this.scrollEndTimer = setTimeout(() => {
        if (this.isTouching) {
          return
        }
        this.isScrolling = false
        if (this.heightPending) {
          this.syncAppHeight()
        }
        this.fixSnapAlignment()
      }, 140)
    },
    // 滚停后未对准吸附点时滚回最近点（仅触屏兜底）
    fixSnapAlignment() {
      // 动画进行中不纠偏
      if (this.pageTween) {
        return
      }
      // 桌面端已由 ScrollTrigger snap 兜底
      if (!this.isSnapPage() || Observer.isTouch === 0) {
        return
      }
      // 与翻页/回弹同口径读取总距离
      const maxScroll = this.getMaxScroll()
      if (maxScroll <= 0) {
        return
      }
      const points = [0, maxScroll]
      document.querySelectorAll('.fullscreen-section').forEach((el) => {
        // 跳过未渲染（display:none）的模块，避免非法吸附点
        if (!el.getClientRects().length) {
          return
        }
        const top = window.scrollY + el.getBoundingClientRect().top
        points.push(Math.min(maxScroll, Math.max(0, top)))
      })
      let nearest = points[0]
      points.forEach((point) => {
        if (Math.abs(point - window.scrollY) < Math.abs(nearest - window.scrollY)) {
          nearest = point
        }
      })
      if (Math.abs(nearest - window.scrollY) < 3) {
        return
      }
      window.scrollTo({ top: nearest, behavior: 'smooth' })
    },
    // 是否启用整屏吸附的页面
    isSnapPage() {
      return ['/', '/product', '/service', '/download', '/about'].indexOf(this.$route.path) > -1
    },
    // 路由变化时重建吸附与翻页实例
    updateSnap() {
      // 终止动画并将 smoother 位置归零，避免新页面从下方滚上来
      if (this.pageTween) {
        this.pageTween.kill()
        this.pageTween = null
      }
      if (this.smoother) {
        this.smoother.scrollTop(0)
      }
      // 全站关闭 CSS scroll-snap，翻页由 JS 控制
      document.documentElement.style.scrollSnapType = ''
      document.body.style.scrollSnapType = ''
      this.updateStSnap()
    },
    // 整屏吸附页统一由 GSAP 翻页接管
    usePageTurn() {
      return this.isSnapPage()
    },
    // 重建滚轮翻页与 ScrollTrigger 吸附；$nextTick 保证只存在一套实例
    updateStSnap() {
      this.$nextTick(() => {
        if (this.snapTrigger) {
          this.snapTrigger.kill()
          this.snapTrigger = null
        }
        if (this.pageObserver) {
          this.pageObserver.kill()
          this.pageObserver = null
        }
        if (!this.usePageTurn()) {
          return
        }
        this.snapTrigger = ScrollTrigger.create({
          trigger: '#smooth-content',
          start: 0,
          end: 'max',
          snap: {
            snapTo: (value) => this.snapToNearestPoint(value),
            // delay 传 0 会被内部兜底成 0.1s，须传极小正值
            duration: { min: 0.15, max: 0.25 },
            delay: 0.001,
            ease: 'power2.out'
          }
        })
        // tolerance 过滤触控板微小滑动；ignoreCheck 保留内层滚动
        this.pageObserver = Observer.create({
          type: 'wheel',
          preventDefault: true,
          tolerance: 25,
          ignoreCheck: (e) => this.shouldIgnoreWheel(e),
          onDown: () => this.turnPage(1),
          onUp: () => this.turnPage(-1)
        })
        // 刷新滚动总长，吸附点换算基准才是最终值
        ScrollTrigger.refresh()
      })
    },
    // 滚到相邻吸附点；时长按距离等比换算，每屏基准 0.65s
    turnPage(direction) {
      if (this.pageTween || !this.smoother) {
        return
      }
      const st = this.snapTrigger
      if (!st || !st.end) {
        return
      }
      // 实时读取总距离，避免停在旧位置
      const maxScroll = this.getMaxScroll()
      if (maxScroll <= 0) {
        return
      }
      const current = this.smoother.scrollTop()
      const points = this.collectSnapPoints(maxScroll).sort((a, b) => a - b)
      let target = null
      if (direction > 0) {
        // 1px 容差，落在吸附点上时取下一个点
        for (let i = 0; i < points.length; i++) {
          if (points[i] > current + 1) {
            target = points[i]
            break
          }
        }
      } else {
        for (let i = points.length - 1; i >= 0; i--) {
          if (points[i] < current - 1) {
            target = points[i]
            break
          }
        }
      }
      if (target === null) {
        return
      }
      const state = { y: current }
      // 下限 0.12s 防止极短距离瞬间跳完
      const duration = Math.max(0.12, (Math.abs(target - current) / window.innerHeight) * 0.65)
      this.pageTween = gsap.to(state, {
        y: target,
        duration,
        ease: 'none',
        onUpdate: () => {
          if (this.smoother) {
            this.smoother.scrollTop(state.y)
          }
        },
        onComplete: () => {
          this.pageTween = null
          // 动画结束时立即同步挂起的高度
          if (this.heightPending) {
            this.syncAppHeight()
          }
        }
      })
    },
    // 触摸翻页：拖动跟手，松手按位移/速度判定翻页或回弹
    onTurnTouchStart(e) {
      this.turnTouchActive = false
      this.turnTouchSkip = true
      this.turnTouchInner = null
      if (!this.usePageTurn() || e.touches.length !== 1) {
        return
      }
      // 记录起点，内层滚到边界后从此处接管
      this.turnTouchStartY = e.touches[0].clientY
      this.turnTouchLastY = e.touches[0].clientY
      this.turnTouchLastTime = Date.now()
      this.turnTouchVelocity = 0
      this.turnTouchPeakVelocity = 0
      // 内层滚动区先交给原生滚动
      this.turnTouchInner = this.findScrollableAncestor(e.target)
      if (this.turnTouchInner) {
        return
      }
      // 动画中重新触摸：以当前位置为新起点接管
      if (this.pageTween) {
        this.pageTween.kill()
        this.pageTween = null
      }
      this.turnTouchSkip = false
      this.turnTouchActive = true
      this.turnTouchStartScroll = this.smoother ? this.smoother.scrollTop() : window.scrollY
    },
    onTurnTouchMove(e) {
      if (this.turnTouchSkip) {
        // 内层未到边界保持原生滚动；到边界后转为翻页，避免惯性跳过多屏
        if (!this.turnTouchInner) {
          return
        }
        const dy = this.turnTouchStartY - e.touches[0].clientY // 手指上移为正 → 页面向下翻
        const el = this.turnTouchInner
        const atTop = el.scrollTop <= 0
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
        // 方向未明确或内层仍可滚动
        if (Math.abs(dy) < 8 || (dy > 0 && !atBottom) || (dy < 0 && !atTop)) {
          return
        }
        if (e.cancelable) {
          e.preventDefault()
        }
        // 终止动画，以当前位置为新起点
        if (this.pageTween) {
          this.pageTween.kill()
          this.pageTween = null
        }
        this.turnTouchSkip = false
        this.turnTouchActive = true
        this.turnTouchStartY = e.touches[0].clientY
        this.turnTouchLastY = e.touches[0].clientY
        this.turnTouchLastTime = Date.now()
        this.turnTouchVelocity = 0
        this.turnTouchPeakVelocity = 0
        this.turnTouchStartScroll = this.smoother ? this.smoother.scrollTop() : window.scrollY
        return
      }
      if (!this.turnTouchActive) {
        return
      }
      // 阻止原生滚动，位置完全由手势驱动
      if (e.cancelable) {
        e.preventDefault()
      }
      const y = e.touches[0].clientY
      const now = Date.now()
      const dt = Math.max(1, now - this.turnTouchLastTime)
      const delta = this.turnTouchLastY - y // 手指上移为正 → 页面向下滚
      const maxScroll = this.getMaxScroll()
      const target = Math.min(maxScroll, Math.max(0, this.turnTouchStartScroll + (this.turnTouchStartY - y)))
      if (this.smoother) {
        this.smoother.scrollTop(target)
      }
      // 速度采样，抑制单帧抖动
      this.turnTouchVelocity = this.turnTouchVelocity * 0.4 + (delta / dt) * 0.6
      if (Math.abs(this.turnTouchVelocity) > Math.abs(this.turnTouchPeakVelocity)) {
        this.turnTouchPeakVelocity = this.turnTouchVelocity
      }
      this.turnTouchLastY = y
      this.turnTouchLastTime = now
    },
    // 松手判定：位移为主，速度为辅
    onTurnTouchEnd() {
      if (!this.turnTouchActive || this.turnTouchSkip) {
        this.turnTouchActive = false
        return
      }
      this.turnTouchActive = false
      if (!this.smoother) {
        return
      }
      const moved = this.smoother.scrollTop() - this.turnTouchStartScroll
      // 位移超屏高 15%，或速度峰值超 0.3px/ms；取峰值避免松手减速误判
      const threshold = window.innerHeight * 0.15
      const velocity = Math.abs(this.turnTouchPeakVelocity) > Math.abs(this.turnTouchVelocity)
        ? this.turnTouchPeakVelocity
        : this.turnTouchVelocity
      if (moved > threshold || (moved > 8 && velocity > 0.3)) {
        this.turnPage(1)
      } else if (moved < -threshold || (moved < -8 && velocity < -0.3)) {
        this.turnPage(-1)
      } else {
        this.snapBack()
      }
    },
    // 未达阈值时滚回最近吸附点
    snapBack() {
      if (!this.smoother) {
        return
      }
      const st = this.snapTrigger
      if (!st || !st.end) {
        return
      }
      // 实时读取总距离，避免停在旧位置
      const maxScroll = this.getMaxScroll()
      if (maxScroll <= 0) {
        return
      }
      const current = this.smoother.scrollTop()
      const points = this.collectSnapPoints(maxScroll)
      let nearest = points[0]
      points.forEach((point) => {
        if (Math.abs(point - current) < Math.abs(nearest - current)) {
          nearest = point
        }
      })
      if (Math.abs(nearest - current) < 1) {
        return
      }
      const state = { y: current }
      // 时长按距离等比换算，每屏基准 0.35s
      const duration = Math.max(0.12, (Math.abs(nearest - current) / window.innerHeight) * 0.35)
      this.pageTween = gsap.to(state, {
        y: nearest,
        duration,
        ease: 'none',
        onUpdate: () => {
          if (this.smoother) {
            this.smoother.scrollTop(state.y)
          }
        },
        onComplete: () => {
          this.pageTween = null
          // 动画结束时同步挂起的高度
          if (this.heightPending) {
            this.syncAppHeight()
          }
        }
      })
    },
    // 向上查找首个可滚动祖先（滚轮与触摸共用）
    findScrollableAncestor(target) {
      let el = target
      while (el && el !== document.body && el !== document.documentElement) {
        const style = getComputedStyle(el)
        if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
          return el
        }
        el = el.parentElement
      }
      return null
    },
    // 内层可滚动区未到边界时放行原生滚动
    shouldIgnoreWheel(e) {
      const el = this.findScrollableAncestor(e.target)
      if (!el) {
        return false
      }
      const atTop = el.scrollTop <= 0
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
      return !((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom))
    },
    // 吸附点：页面顶 + 各整屏模块顶部 + 页面底（实时换算坐标）
    collectSnapPoints(maxScroll) {
      const limit = typeof maxScroll === 'number' ? maxScroll : this.getMaxScroll()
      const points = [0, limit]
      // 触摸端用实时坐标；桌面端平滑滚动中 rect 滞后，用 offset
      const useRect = Observer.isTouch > 0 || !this.smoother
      const base = this.smoother ? this.smoother.scrollTop() : window.scrollY
      document.querySelectorAll('.fullscreen-section').forEach((el) => {
        // 跳过未渲染（display:none）的模块，避免非法吸附点
        if (!el.getClientRects().length) {
          return
        }
        const top = useRect ? base + el.getBoundingClientRect().top : this.smoother.offset(el, 'top top')
        points.push(Math.min(limit, Math.max(0, top)))
      })
      return points
    },
    // snapTo 回调：找最近吸附点并换算回 progress
    snapToNearestPoint(value) {
      const st = this.snapTrigger
      if (!st || !st.end) {
        return value
      }
      const maxScroll = st.end - st.start
      if (maxScroll <= 0) {
        return value
      }
      // 终点与实际高度不一致说明页面高度变过（如语言切换隐藏模块），先刷新再放行
      if (Math.abs(maxScroll - this.getMaxScroll()) > 1) {
        ScrollTrigger.refresh()
        return value
      }
      // 动画进行中不吸附，避免与动画抢滚动位置
      if (this.pageTween) {
        return value
      }
      const points = this.collectSnapPoints(maxScroll)
      // 已在吸附点上时返回原 progress，避免二次 tween
      const current = this.smoother ? this.smoother.scrollTop() : window.scrollY
      if (points.some((point) => Math.abs(point - current) < 2)) {
        return value
      }
      const target = Math.min(maxScroll, Math.max(0, value * maxScroll))
      let nearest = points[0]
      points.forEach((point) => {
        if (Math.abs(point - target) < Math.abs(nearest - target)) {
          nearest = point
        }
      })
      return Math.min(1, Math.max(0, (nearest - st.start) / maxScroll))
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  height: 100%;
  /* 安全区变量，供 JS 计算有效高度 */
  --safe-top: env(safe-area-inset-top, 0px);
  --safe-bottom: env(safe-area-inset-bottom, 0px);
  --safe-left: env(safe-area-inset-left, 0px);
  --safe-right: env(safe-area-inset-right, 0px);
}

body {
  font-family: 'Noto Sans SC', 'PingFang SC', sans-serif;
  overflow-x: hidden;
}

/* i18n 文案中的 \n 换行 */
h1, h2, h3, h4, h5, h6, p, span, a, li, td, th, button, label {
  white-space: pre-line;
}

/* 图片不可选中 */
img {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* 隐藏滚动条，保留滚动 */
::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

html {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
</style>
